/**
 * Custom hook for managing user subscription status
 * 
 * Fetches subscription status from backend and provides:
 * - isPro: boolean indicating if user has Pro subscription
 * - subscriptionStatus: "free" | "pro"
 * - storageLimit: number | null (null = unlimited)
 * - generationsRemaining: number | null (null = unlimited)
 * - loading: boolean indicating if status is being fetched
 * - error: any error that occurred
 * - refresh: function to manually refresh subscription status
 */

import { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get session ID from sessionStorage or generate a new one
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('sessionId');
  if (!sessionId) {
    sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('sessionId', sessionId);
  }
  return sessionId;
};

/**
 * Custom hook for subscription status
 * 
 * @param {Object} user - Firebase user object (null if not authenticated)
 * @returns {Object} Subscription status and utilities
 */
export const useSubscription = (user) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [storageLimit, setStorageLimit] = useState(null);
  const [generationsRemaining, setGenerationsRemaining] = useState(null);
  const [designCount, setDesignCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!user && !getSessionId()) {
      // No user and no session, set defaults
      setSubscriptionStatus('free');
      setIsPro(false);
      setStorageLimit(50);
      setGenerationsRemaining(2);
      setDesignCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add session ID (always include for rate limiting)
      const sessionId = getSessionId();
      if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      // Add auth headers if user is authenticated
      if (user) {
        // Get Firebase ID token
        try {
          const token = await user.getIdToken();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (err) {
          console.warn('Could not get auth token:', err);
        }
        headers['X-User-ID'] = user.uid;
      }

      const response = await fetch(`${API_URL}/api/user/status`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch subscription status: ${response.statusText}`);
      }

      const data = await response.json();

      setSubscriptionStatus(data.subscription_status || 'free');
      setIsPro(data.is_pro || false);
      setStorageLimit(data.storage_limit);
      setGenerationsRemaining(data.generations_remaining);
      setDesignCount(data.design_count || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      setError(err.message);
      // Set defaults on error
      setSubscriptionStatus('free');
      setIsPro(false);
      setStorageLimit(50);
      setGenerationsRemaining(2);
      setDesignCount(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch subscription status on mount and when user changes
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  return {
    subscriptionStatus,
    isPro,
    storageLimit,
    generationsRemaining,
    designCount,
    loading,
    error,
    refresh,
  };
};

/**
 * Helper function to check if user can generate (has remaining generations)
 * 
 * @param {Object} subscriptionData - Data from useSubscription hook
 * @returns {boolean} True if user can generate
 */
export const canGenerate = (subscriptionData) => {
  if (!subscriptionData) return false;
  if (subscriptionData.isPro) return true; // Pro users: unlimited
  if (subscriptionData.generationsRemaining === null) return true; // Unlimited
  return subscriptionData.generationsRemaining > 0;
};

/**
 * Helper function to check if user can save designs
 * 
 * @param {Object} subscriptionData - Data from useSubscription hook
 * @returns {boolean} True if user can save
 */
export const canSave = (subscriptionData) => {
  if (!subscriptionData) return false;
  if (!subscriptionData.storageLimit) return true; // Unlimited storage
  return subscriptionData.designCount < subscriptionData.storageLimit;
};

/**
 * Helper function to get remaining storage
 * 
 * @param {Object} subscriptionData - Data from useSubscription hook
 * @returns {number | null} Remaining storage slots (null = unlimited)
 */
export const getRemainingStorage = (subscriptionData) => {
  if (!subscriptionData) return null;
  if (!subscriptionData.storageLimit) return null; // Unlimited
  return Math.max(0, subscriptionData.storageLimit - subscriptionData.designCount);
};

